import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Play, History, Loader2, Zap, Sparkles, Layers, Workflow as WorkflowIcon } from 'lucide-react';
import { apiRequest } from '../../config/api';
import { useKeyboardShortcut, getModKey } from '../../hooks/useKeyboardShortcuts';
import NodePalette from './NodePalette';
import NodeConfigPanel from './NodeConfigPanel';
import RunHistoryPanel from './RunHistoryPanel';
import ExplainPanel from './ExplainPanel';
import CanvasWorkflowView from './CanvasWorkflowView';
import SimpleWorkflowView from './SimpleWorkflowView';
import useWorkflowState from './useWorkflowState';
import useExplainStream from '../../hooks/useExplainStream';
import { buildSimpleLayout } from './simpleLayout';
import { NODE_TYPES } from './nodeTypes';

const VIEW_MODE_STORAGE_KEY = 'wf:viewMode';

/**
 * If the user has expressed a preference for this workflow or globally,
 * honor it. Otherwise pick a sensible default for the workflow's shape:
 *   - Simple for ≤5 nodes and no cycles (the use case Simple Mode shines
 *     at — small flows that read better as a vertical card list).
 *   - Builder for everything else.
 */
function readInitialViewMode(workflow) {
  try {
    const perWorkflow = localStorage.getItem(`wf:${workflow.id}:viewMode`);
    if (perWorkflow === 'builder' || perWorkflow === 'simple') return perWorkflow;
    const global = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    if (global === 'builder' || global === 'simple') return global;
  } catch {
    /* localStorage may be unavailable */
  }

  const def = workflow.definition || {};
  const nodes = def.nodes || [];
  const edges = def.edges || [];
  if (nodes.length === 0) return 'builder';
  if (nodes.length > 5) return 'builder';
  try {
    const { roots } = buildSimpleLayout({ nodes, edges, nodeConfigs: def.nodeConfigs || {} });
    const hasCycle = roots.some((r) =>
      r.chain.some((c) => c.kind === 'cycle-warning')
    );
    if (hasCycle) return 'builder';
  } catch {
    return 'builder';
  }
  return 'simple';
}

const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: false,
  markerEnd: { type: 'arrowclosed', color: '#d1d5db', width: 16, height: 16 },
  style: { stroke: '#e5e7eb', strokeWidth: 1.5 },
};

export default function WorkflowBuilder({ workflow, onSave, onStatusChange, saving }) {
  const navigate = useNavigate();

  // Shared data layer (used by both Canvas Mode and the future Simple Mode).
  const state = useWorkflowState(workflow, { defaultEdgeOptions });
  const {
    nodes,
    edges,
    nodeConfigs,
    nodeOutputs,
    workflowName,
    setWorkflowName,
    deleteNode,
    updateNodeConfig,
    setNodeOutput,
    mergeNodeOutputSchema,
    serialize,
  } = state;

  const [selectedNodeId, setSelectedNodeId] = useState(null);

  // View mode: 'builder' (Canvas) or 'simple' (vertical card list).
  const [viewMode, setViewMode] = useState(() => readInitialViewMode(workflow));
  const setViewModeAndPersist = useCallback(
    (mode) => {
      setViewMode(mode);
      try {
        localStorage.setItem(`wf:${workflow.id}:viewMode`, mode);
        localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
      } catch {
        /* ignore */
      }
    },
    [workflow.id]
  );

  // Run state
  const [running, setRunning] = useState(false);
  const [activeRunId, setActiveRunId] = useState(null);
  const [activeRunNodeStatuses, setActiveRunNodeStatuses] = useState({});

  // Right-side panel coordination: only one of 'config' | 'history' | 'explain'
  // can be open at a time. 'config' is only meaningful when a node is selected.
  const [rightPanel, setRightPanel] = useState(null);
  const [highlightedNodeId, setHighlightedNodeId] = useState(null);

  const openConfigForNode = useCallback((nodeId) => {
    setSelectedNodeId(nodeId);
    setRightPanel('config');
  }, []);

  const toggleRightPanel = useCallback((name) => {
    setRightPanel((prev) => (prev === name ? null : name));
  }, []);

  // Lift the Explain conversation state up so it survives panel close/reopen.
  const explainStream = useExplainStream({ workflowId: workflow.id });

  // ReactFlow instance is owned by CanvasWorkflowView; bubble it up so the
  // Explain panel's "view step" can pan the canvas.
  const reactFlowInstanceRef = useRef(null);
  const handleReactFlowInit = useCallback((instance) => {
    reactFlowInstanceRef.current = instance;
  }, []);

  // Clicking a step in the Explain panel pans the canvas and pulses the node.
  // We intentionally do NOT switch to the Config panel here — that would close
  // the Explain panel and lose the conversation. The user can click the node
  // on the canvas to open Config when they want to edit.
  const highlightTimerRef = useRef(null);
  const handleHighlightNode = useCallback(
    (nodeId) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;
      setHighlightedNodeId(nodeId);
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = setTimeout(() => setHighlightedNodeId(null), 2200);

      // Builder mode: pan the canvas. Simple mode has no canvas — the
      // amber ring around the matching card is enough.
      if (viewMode === 'builder') {
        const instance = reactFlowInstanceRef.current;
        if (instance && node.position) {
          instance.setCenter(
            node.position.x + 90,
            node.position.y + 25,
            { duration: 350, zoom: instance.getZoom() }
          );
        }
      }
    },
    [nodes, viewMode]
  );

  // Clear the highlight timer on unmount.
  useEffect(() => {
    return () => {
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
    };
  }, []);

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId),
    [nodes, selectedNodeId]
  );

  // BFS backward from selectedNodeId to find all upstream (ancestor) nodes
  // — used by NodeConfigPanel to populate {{nodeId.path}} reference pickers.
  const upstreamNodes = useMemo(() => {
    if (!selectedNodeId) return [];
    const visited = new Set();
    const queue = [selectedNodeId];
    while (queue.length > 0) {
      const current = queue.shift();
      for (const edge of edges) {
        if (edge.target === current && !visited.has(edge.source)) {
          visited.add(edge.source);
          queue.push(edge.source);
        }
      }
    }
    return Array.from(visited)
      .map((id) => {
        const node = nodes.find((n) => n.id === id);
        if (!node) return null;
        const typeDef = NODE_TYPES[node.data.nodeType];
        if (!typeDef) return null;
        const dynamicSchema = nodeOutputs[id]?.schema;
        const schema = dynamicSchema || typeDef.outputSchema;
        if (!schema?.length) return null;
        return {
          id,
          label: node.data.label || typeDef.label,
          nodeType: node.data.nodeType,
          color: typeDef.color,
          icon: typeDef.icon,
          outputSchema: schema,
        };
      })
      .filter(Boolean);
  }, [selectedNodeId, edges, nodes, nodeOutputs]);

  const handleConfigChange = useCallback(
    (newConfig) => updateNodeConfig(selectedNodeId, newConfig),
    [selectedNodeId, updateNodeConfig]
  );

  const handleTestRun = useCallback(
    (nodeId, data, schema) => setNodeOutput(nodeId, data, schema),
    [setNodeOutput]
  );

  const handleDeleteNode = useCallback(() => {
    if (!selectedNodeId) return;
    deleteNode(selectedNodeId);
    setSelectedNodeId(null);
  }, [selectedNodeId, deleteNode]);

  const handleSave = useCallback(() => {
    onSave(serialize(), workflowName);
  }, [serialize, workflowName, onSave]);

  // Cmd+S / Ctrl+S → save workflow
  useKeyboardShortcut(
    's',
    () => { if (!saving) handleSave(); },
    { mod: true, allowInInput: true },
    [saving, handleSave]
  );

  const handleRun = useCallback(async () => {
    if (running) return;
    setRunning(true);
    try {
      await onSave(serialize(), workflowName);
      const resp = await apiRequest(`/workflows/${workflow.id}/run`, {
        method: 'POST',
        body: JSON.stringify({ trigger_type: 'manual' }),
      });
      if (resp.ok) {
        const run = await resp.json();
        setActiveRunId(run.id);
        setActiveRunNodeStatuses({});
        setRightPanel('history');
      } else {
        const err = await resp.json().catch(() => ({}));
        console.error('Failed to start run:', err);
      }
    } catch (error) {
      console.error('Error starting workflow run:', error);
    } finally {
      setRunning(false);
    }
  }, [running, serialize, workflowName, onSave, workflow.id]);

  const handleNodeStatusesUpdate = useCallback((statuses) => {
    setActiveRunNodeStatuses(statuses);
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedNodeId(null);
  }, []);

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - var(--app-chrome-height, 0px))' }}>
      {/* Top bar */}
      <div className="h-11 border-b border-gray-100 bg-white flex items-center px-4 gap-3 flex-shrink-0">
        <button
          onClick={() => navigate('/workflows')}
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <input
          type="text"
          value={workflowName}
          onChange={(e) => setWorkflowName(e.target.value)}
          className="text-[13px] font-medium text-gray-900 bg-transparent border-none outline-none focus:ring-0 px-1 py-0.5 rounded hover:bg-gray-50 focus:bg-gray-50 transition-colors min-w-0 flex-1 max-w-xs"
        />
        {/* View toggle: Builder (canvas) vs Simple (vertical list) */}
        <div className="inline-flex items-center bg-gray-50 border border-gray-200 rounded-lg p-0.5">
          <button
            onClick={() => setViewModeAndPersist('builder')}
            title="Builder mode (free-form canvas)"
            className={`inline-flex items-center gap-1.5 px-2 py-1 text-[12px] font-medium rounded-md transition-colors ${
              viewMode === 'builder'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <WorkflowIcon className="w-3 h-3" />
            Builder
          </button>
          <button
            onClick={() => setViewModeAndPersist('simple')}
            title="Simple mode (step-by-step view)"
            className={`inline-flex items-center gap-1.5 px-2 py-1 text-[12px] font-medium rounded-md transition-colors ${
              viewMode === 'simple'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Layers className="w-3 h-3" />
            Simple
          </button>
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          {/* Explain (AI walkthrough) */}
          <button
            onClick={() => toggleRightPanel('explain')}
            disabled={nodes.length === 0}
            title="Get an AI explanation of this workflow"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[13px] font-medium rounded-lg transition-all border disabled:opacity-40 disabled:cursor-not-allowed ${
              rightPanel === 'explain'
                ? 'text-violet-700 bg-violet-50 border-violet-200'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 border-gray-200'
            }`}
          >
            <Sparkles className="w-3.5 h-3.5" />
            Explain
          </button>
          {/* Activate / Deactivate toggle */}
          <button
            onClick={() => onStatusChange(workflow.status === 'active' ? 'draft' : 'active')}
            disabled={saving}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[13px] font-medium rounded-lg transition-all ${
              workflow.status === 'active'
                ? 'text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 border border-gray-200'
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            <Zap className={`w-3.5 h-3.5 ${workflow.status === 'active' ? 'fill-emerald-500 text-emerald-500' : ''}`} />
            {workflow.status === 'active' ? 'Active' : 'Activate'}
          </button>
          <div className="w-px h-5 bg-gray-100" />
          <button
            onClick={() => toggleRightPanel('history')}
            className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[13px] font-medium rounded-lg transition-colors ${
              rightPanel === 'history'
                ? 'text-gray-900 bg-gray-100'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <History className="w-3.5 h-3.5" />
            History
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            title={`Save (${getModKey()}+S)`}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[13px] font-medium rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-50 border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button
            onClick={handleRun}
            disabled={running || saving || nodes.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium rounded-lg text-white bg-gray-900 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors active:scale-[0.97]"
          >
            {running ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Play className="w-3.5 h-3.5" />
            )}
            {running ? 'Running...' : 'Run'}
          </button>
        </div>
      </div>

      {/* Builder body */}
      <div className="flex-1 flex overflow-hidden">
        {viewMode === 'builder' && <NodePalette />}

        {viewMode === 'builder' ? (
          <CanvasWorkflowView
            state={state}
            defaultEdgeOptions={defaultEdgeOptions}
            selectedNodeId={selectedNodeId}
            highlightedNodeId={highlightedNodeId}
            activeRunId={activeRunId}
            activeRunNodeStatuses={activeRunNodeStatuses}
            onSelectNode={openConfigForNode}
            onClearSelection={handleClearSelection}
            onReactFlowInit={handleReactFlowInit}
          />
        ) : (
          <SimpleWorkflowView
            state={state}
            selectedNodeId={selectedNodeId}
            highlightedNodeId={highlightedNodeId}
            activeRunNodeStatuses={activeRunNodeStatuses}
            onSelectNode={openConfigForNode}
            onSwitchToBuilder={() => setViewModeAndPersist('builder')}
          />
        )}

        {/* Config panel */}
        {selectedNode && rightPanel === 'config' && (
          <NodeConfigPanel
            node={selectedNode}
            config={nodeConfigs[selectedNodeId]}
            onChange={handleConfigChange}
            onDelete={handleDeleteNode}
            onClose={() => {
              setSelectedNodeId(null);
              setRightPanel(null);
            }}
            upstreamNodes={upstreamNodes}
            onTestRun={handleTestRun}
            testOutput={nodeOutputs[selectedNodeId] || null}
            workflowId={workflow?.id}
            onSaveAndNavigate={(path = '/chat') => {
              handleSave();
              navigate(path);
            }}
            onOutputSchemaUpdate={(schema) => mergeNodeOutputSchema(selectedNodeId, schema)}
          />
        )}

        {/* Run History panel */}
        {rightPanel === 'history' && (
          <RunHistoryPanel
            workflowId={workflow.id}
            activeRunId={activeRunId}
            onSelectRun={setActiveRunId}
            onNodeStatusesUpdate={handleNodeStatusesUpdate}
            onClose={() => setRightPanel(null)}
          />
        )}

        {/* Explain panel */}
        {rightPanel === 'explain' && (
          <ExplainPanel
            workflowDefinition={{
              nodes: nodes.map(({ id, position, data }) => ({ id, position, data })),
              edges,
              nodeConfigs,
            }}
            explainStream={explainStream}
            onHighlightNode={handleHighlightNode}
            onClose={() => setRightPanel(null)}
          />
        )}
      </div>
    </div>
  );
}
